import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, forwardRef } from 'react';
import { RadioInput, CheckboxInput, BasicInput, Dropdown, TextareaInput, RangeInput, FileInput, RichTextInput, RadioButtonInput } from '../components';
/**
 * `useInputComponent` is a custom hook that dynamically renders different types of input components
 * based on the provided configuration. It leverages the `react-hook-form` for form state management
 * and validation, and supports a variety of input types including basic text, radio buttons, checkboxes,
 * dropdowns, text areas, and range inputs. This hook abstracts the complexity of handling different
 * input types, providing a unified interface for form inputs.
 *
 * @param {CustomField<FieldValues>} field - The field configuration object which includes properties
 * like type, label, validation rules, etc.
 * @param {UseFormSetValue<FieldValues>} setValue - A `setValue` method from `react-hook-form` used
 * to update form values.
 * @param {FieldValues} formValues - The current values of the form, used for controlling input components.
 * @returns - `InputComponent` containing props, such as `onChange` handlers, validation rules, etc.
 *
 * The hook intelligently selects the appropriate input component (e.g., `BasicInput`, `RadioInput`, etc.)
 * based on the `type` property of the `field` parameter. It also processes validation rules and other
 * properties specified in `field`, ensuring that the returned input component is fully configured for use
 * within a form. Additional utility functions like `getValidationMessage`, `mapEnumToDropdownItems`,
 * and validation processing functions in the `./utils` folder are used to support this functionality.
 */
export const useInputComponent = (definition, setValue) => {
    return useMemo(() => {
        const commonProps = {
            key: definition.id,
            label: definition.label,
            type: definition.fieldType,
            readOnly: !!definition.readOnly,
            disabled: definition.disabled
        };
        switch (definition.fieldType) {
            case 'multiSelect':
            case 'singleSelect':
                const DropdownComponent = forwardRef((props, ref) => (_jsx(Dropdown, Object.assign({}, props, commonProps, { ref: ref, options: definition.options, isLoading: definition.isLoading, setValue: setValue }))));
                DropdownComponent.displayName = 'DropdownComponent';
                return DropdownComponent;
            case 'radio':
                const RadioComponent = forwardRef((props, ref) => (_jsx(RadioInput, Object.assign({}, props, commonProps, { options: definition.options, ref: ref }))));
                RadioComponent.displayName = 'RadioComponent';
                return RadioComponent;
            case 'radioButton':
                const RadioButtonGroupComponent = forwardRef((props, ref) => (_jsx(RadioButtonInput, Object.assign({}, props, commonProps, { options: definition.options, ref: ref }))));
                RadioButtonGroupComponent.displayName = 'RadioComponent';
                return RadioButtonGroupComponent;
            case 'range':
                const RangeComponent = forwardRef((props, ref) => {
                    var _a, _b;
                    return (_jsx(RangeInput, Object.assign({}, props, commonProps, { min: (_a = definition.validation) === null || _a === void 0 ? void 0 : _a.min, max: (_b = definition.validation) === null || _b === void 0 ? void 0 : _b.max, ref: ref })));
                });
                RangeComponent.displayName = 'RangeComponent';
                return RangeComponent;
            case 'checkbox':
                const CheckboxComponent = forwardRef((props, ref) => (_jsx(CheckboxInput, Object.assign({}, props, commonProps, { ref: ref }))));
                CheckboxComponent.displayName = 'CheckboxComponent';
                return CheckboxComponent;
            case 'longText':
                const TextreaComponent = forwardRef((props, ref) => (_jsx(TextareaInput, Object.assign({}, props, commonProps, { ref: ref }))));
                TextreaComponent.displayName = 'TextareaComponent';
                return TextreaComponent;
            case 'file':
                const FileInputComponent = forwardRef((props, ref) => (_jsx(FileInput, Object.assign({}, props, commonProps, { ref: ref, setValue: setValue }))));
                FileInputComponent.displayName = 'FileInputComponent';
                return FileInputComponent;
            case 'richText':
                const RichTextComponent = forwardRef((props, ref) => (_jsx(RichTextInput, Object.assign({}, props, commonProps, { ref: ref }))));
                RichTextComponent.displayName = 'RichTextComponent';
                return RichTextComponent;
            default:
                const BasicComponent = forwardRef((props, ref) => (_jsx(BasicInput, Object.assign({}, props, commonProps, { decimalPlaces: definition.decimalPlaces, ref: ref }))));
                BasicComponent.displayName = 'BasicComponent';
                return BasicComponent;
        }
    }, [definition, setValue]);
};
